import { useEffect, useState } from "react";
import { TimeSlot, BookingLanguage } from "@/types/booking";
import { calendarIcon, binIcon } from "@/assets/images";
import { ChevronDown, ChevronUp, Edit2, Check, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { t } from "@/locales";

interface SlotPreferenceBlockProps {
  slotNumber: 1 | 2;
  language: BookingLanguage;
  selectedSlot?: TimeSlot;
  isExpanded: boolean;
  onEdit?: () => void;
  onDone?: () => void;
  onDelete?: () => void;
  children?: React.ReactNode;
  hasFirstSlot?: boolean; // Whether first slot is selected (for second slot UI)
  isLoading?: boolean; // Whether external slots are loading
}

const SlotPreferenceBlock = ({
  slotNumber,
  language,
  selectedSlot,
  isExpanded,
  onEdit,
  onDone,
  onDelete,
  children,
  hasFirstSlot = false,
  isLoading = false,
}: SlotPreferenceBlockProps) => {
  const isFirstSlot = slotNumber === 1;

  // After first paint, enable CSS transitions for user interactions
  const [animate, setAnimate] = useState(false);
  useEffect(() => {
    const raf = requestAnimationFrame(() => setAnimate(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  const transition = animate ? "transition-all ease-in-out" : "";

  // Use centralized translations
  const tr = {
    firstSlotPreference: t(language, "slotPreference.firstSlotPreference"),
    firstSlotSubtitle: t(language, "slotPreference.firstSlotSubtitle"),
    secondSlotPreference: t(language, "slotPreference.secondSlotPreference"),
    secondSlotSubtitle: t(language, "slotPreference.secondSlotSubtitle"),
    optional: t(language, "common.optional"),
    edit: t(language, "common.edit"),
    done: t(language, "common.done"),
    add: t(language, "common.add"),
    fillFirstSlot: t(language, "slotPreference.fillFirstSlot"),
    loading: t(language, "common.loading"),
  };

  // When loading, force second slot collapsed but keep first slot expanded
  // to avoid a collapse-then-expand animation on mount
  const effectiveIsExpanded = isLoading && !isFirstSlot ? false : isExpanded;
  const title = isFirstSlot ? tr.firstSlotPreference : tr.secondSlotPreference;

  const formatDate = (date: Date, language: BookingLanguage) => {
    return date.toLocaleDateString(language, {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onEdit) {
      onEdit();
    }
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDelete) {
      onDelete();
    }
  };

  const handleDoneClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onDone) {
      onDone();
    }
  };

  // For second slot without selection, make the whole block clickable
  const isEmptySecondSlot =
    !isFirstSlot && !selectedSlot && !effectiveIsExpanded && !isLoading;

  return (
    <div
      className={`w-full bg-white border border-gray-100 overflow-hidden rounded-[20px] shadow-card ${transition} duration-300 ${
        isEmptySecondSlot ? "cursor-pointer hover:border-light-blue-300" : ""
      }`}
      onClick={isEmptySecondSlot ? handleEditClick : undefined}
    >
      {/* Header */}
      <div
        className={`flex justify-between items-center gap-4 ${
          effectiveIsExpanded
            ? "pt-[16px] px-[16px] pb-[12px]"
            : "py-[16px] px-[16px]"
        }`}
      >
        <div className="flex flex-col gap-1">
          <div className="flex items-end gap-2">
            <h3 className="text-[16px] font-normal text-neutral-900">
              {title}
            </h3>
            {!isFirstSlot && (
              <span className="text-[14px] mb-[1px] font-normal text-neutral-900 italic opacity-60 inline">
                {tr.optional}
              </span>
            )}
          </div>
          {isFirstSlot ? (
            <p className="text-[14px] text-neutral-900 opacity-60">
              {tr.firstSlotSubtitle}
            </p>
          ) : (
            <p className="text-[14px] text-neutral-900 opacity-60">
              {tr.secondSlotSubtitle}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 relative">
          {/* Show Done button when expanded and slot is selected */}
          {effectiveIsExpanded && selectedSlot && onDone && (
            <Button
              type="button"
              onClick={handleDoneClick}
              size="sm"
              className="h-8 px-3 bg-light-blue-300 hover:bg-light-blue-400 text-neutral-900 font-normal relative gap-0"
            >
              <Check className="w-4 h-4 mr-1" />
              {tr.done}
            </Button>
          )}
        </div>
      </div>

      {/* Content - Expanded state */}
      <div
        className={`overflow-visible ${transition} ${
          effectiveIsExpanded
            ? "max-h-[4000px] duration-700"
            : "max-h-0 duration-[490ms] delay-[105ms]"
        }`}
      >
        <div
          className={`px-[16px] pb-[16px] ${animate ? "transition-opacity ease-in-out" : ""} ${
            effectiveIsExpanded
              ? "opacity-100 duration-500 delay-200 visible"
              : "opacity-0 duration-150 invisible"
          }`}
        >
          {children}
        </div>
      </div>

      {/* Collapsed state showing selected slot OR empty clickable state OR loading */}
      <div
        className={`px-[16px] pb-[16px] ${transition} overflow-hidden ${
          !effectiveIsExpanded
            ? "opacity-100 max-h-[200px] duration-300 delay-[600ms]"
            : "opacity-0 max-h-0 duration-150"
        }`}
      >
        {isLoading && isFirstSlot ? (
          // Loading state - show skeleton
          <div className="flex items-center gap-3 animate-pulse">
            <div className="w-10 h-10 bg-gray-200 rounded-lg flex-shrink-0" />
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-3/4" />
            </div>
          </div>
        ) : selectedSlot ? (
          // Show selected slot with edit/delete buttons
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex items-center flex-1">
              {/* Calendar Icon */}
              <div className="w-10 h-10 bg-light-blue-300 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                <img src={calendarIcon} alt="Calendar" className="w-5 h-5" />
              </div>

              {/* Date and Time */}
              <div className="flex-1">
                <div
                  className="text-[14px] text-neutral-900 font-normal"
                  data-testid={isFirstSlot ? "firstDate" : "secondDate"}
                >
                  {formatDate(selectedSlot.date, language)}, {selectedSlot.time}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-2 relative self-end md:self-auto">
              {/* Edit Button */}
              {onEdit && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleEditClick}
                  className="h-8 px-3 hover:bg-gray-100 text-neutral-900 font-normal relative"
                >
                  <Edit2 className="w-4 h-4 mr-1" />
                  {tr.edit}
                </Button>
              )}

              {/* Delete Button - only for second slot */}
              {!isFirstSlot && onDelete && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDeleteClick}
                  className="h-8 w-8 p-0 hover:bg-red-50 hover:text-red-500 flex-shrink-0 relative"
                >
                  <img src={binIcon} alt="Delete" className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        ) : (
          // Empty state - show Add button if first slot selected, else placeholder text
          !isFirstSlot &&
          (hasFirstSlot ? (
            <Button
              type="button"
              onClick={handleEditClick}
              size="sm"
              className="h-8 px-3 bg-white hover:bg-gray-50 border border-light-blue-300 text-neutral-900 font-normal relative"
            >
              <Plus className="w-4 h-4 mr-1" />
              {tr.add}
            </Button>
          ) : (
            <div className="text-[14px] text-neutral-900 italic opacity-60 text-left">
              {tr.fillFirstSlot}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default SlotPreferenceBlock;
